// CSCAuth.jsx
import React, { useEffect, useState } from "react";

// CONFIG
const BACKEND = "http://localhost:6002";
const CSC_LOGIN_URL = BACKEND + "/auth/csc";

// This component handles:
// - Login with CSC
// - Callback detection
// - Fetching /me
// - Linking CSC if required
export default function CSCAuth() {
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const [linking, setLinking] = useState(false);
  const [cscUserIdInput, setCscUserIdInput] = useState("");
  const [linkMessage, setLinkMessage] = useState(null);

  // 🔎 STEP: When component loads → fetch /me to check if logged in
  useEffect(() => {
    fetchUser();
  }, []);

  async function fetchUser() {
    setLoading(true);
    setErrorMsg(null);
    try {
      const resp = await fetch(`${BACKEND}/me`, {
        credentials: "include",
      });
      if (!resp.ok) {
        setLoading(false);
        return; // user not logged in
      }
      const data = await resp.json();
      setUser(data.user || data);
    } catch (err) {
      console.error("Fetch /me error:", err);
      setErrorMsg("Unable to fetch user.");
    } finally {
      setLoading(false);
    }
  }

  // 🔥 STEP: Start CSC Login
  function startCSCLogin() {
    window.location.href = CSC_LOGIN_URL;
  }

  // 🔗 STEP: Link CSC to existing user
  async function linkCSCAccount(e) {
    e.preventDefault();
    setLinkMessage(null);
    setLinking(true);

    try {
      const resp = await fetch(`${BACKEND}/link/csc`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cscUserId: cscUserIdInput }),
      });

      const data = await resp.json();
      if (!resp.ok || data.success === false) {
        throw new Error(data.message || "Linking failed");
      }

      setLinkMessage("CSC account linked successfully ✔");
      fetchUser();
    } catch (err) {
      setLinkMessage("Error: " + err.message);
    } finally {
      setLinking(false);
    }
  }

  // UI LOGIC
  if (loading) return <div>Loading...</div>;

  return (
    <div style={{ padding: 20, maxWidth: 500 }}>
      <h2>CSC Authentication</h2>

      {/* ⛔ Display error */}
      {errorMsg && <p style={{ color: "red" }}>{errorMsg}</p>}

      {/* ⭐ If not logged in → show CSC login button */}
      {!user && (
        <button
          onClick={startCSCLogin}
          style={{
            padding: "10px 17px",
            background: "#0b5cff",
            color: "#fff",
            borderRadius: 6,
            border: "none",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          Login with CSC
        </button>
      )}

      {/* ⭐ If logged in → show user details */}
      {user && (
        <div style={{ marginTop: 20 }}>
          <h3>Welcome, {user.name || "User"} 🎉</h3>

          <p><strong>Email:</strong> {user.email || "—"}</p>
          <p><strong>Provider:</strong> {user.provider}</p>
          <p><strong>CSC User ID:</strong> {user.cscUserId || "Not linked"}</p>
          <p><strong>Wallet ID:</strong> {user.walletId || "—"}</p>
          <p><strong>Wallet Balance:</strong> {user.lastWalletBalance ?? "—"}</p>

          {/* If CSC NOT linked → show linking form */}
          {!user.cscUserId && (
            <div style={{ marginTop: 20 }}>
              <h4>Link your CSC Account</h4>
              <form onSubmit={linkCSCAccount}>
                <input
                  type="text"
                  placeholder="Enter CSC User ID"
                  value={cscUserIdInput}
                  onChange={(e) => setCscUserIdInput(e.target.value)}
                  style={{
                    padding: 8,
                    width: "100%",
                    marginBottom: 10,
                    borderRadius: 4,
                    border: "1px solid #ccc",
                  }}
                />
                <button
                  type="submit"
                  disabled={linking}
                  style={{
                    padding: "8px 15px",
                    background: linking ? "#aaa" : "#0b5cff",
                    color: "#fff",
                    border: "none",
                    borderRadius: 5,
                    cursor: linking ? "not-allowed" : "pointer",
                  }}
                >
                  {linking ? "Linking..." : "Link CSC"}
                </button>
              </form>

              {linkMessage && (
                <p
                  style={{
                    marginTop: 10,
                    color: linkMessage.startsWith("Error") ? "red" : "green",
                  }}
                >
                  {linkMessage}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
